import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { isEmpty } from 'lodash';
import ActivityHeaderLayout from '@monorepo/shared/src/components/activities/ActivityHeaderLayout';
import DoublePagePdf from '@monorepo/shared/src/components/common/Pdf/DoublePagePdf';
import {
  getActivePages,
  getPageNumber,
} from '@monorepo/shared/src/components/common/Pdf/util/pdf-util';
import PageNavigation from '@monorepo/shared/src/components/common/PageNavigation';
import ToggleButtonGroup from '@monorepo/shared/src/components/common/ToggleButtonGroup';
import WithQuery from '@monorepo/shared/src/components/common/WithQuery';
import MainActivityPagingControls from '@monorepo/shared/src/components/modules/Paging/components/MainActivityPagingControls';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import useLetterTeaching from '@monorepo/shared/src/hooks/useLetterTeaching';
import usePage from '@monorepo/shared/src/hooks/use-page';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { tA11y } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { findPageAndPopulatePageData } from '@monorepo/shared/src/util/book-util';
import {
  ApplicationSection,
  BookType,
  Character,
  RouteSource,
  PageLayout,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { formatCharacter } from '@monorepo/shared/src/util/telemetry-util';
import SectionFooter from 'components/layout/SectionFooter';
import useTrialUserOmitRoutes from 'state/hooks/use-trial-user-omit-routes';
import { StudentPracticeBookLevel } from 'types';

const t = (key: string, options?) => translate('letterTeaching', key, options);

const PracticeBook = (props): React.ReactElement => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_PAGE);
  const location = useLocation();
  const isMainPage = window.location.pathname === '/student-practice-book';
  const [{ books, loading, error, refetch }] = usePageStore(BookType.PRACTICE_BOOK, props.letter);
  const [practiceBookType, setPracticeBookType] = useState(StudentPracticeBookLevel.Emerging);
  const [character, setCharacter] = useState<Character | null>(null);
  const [{ currentPage, totalPages, noOfPages }, { goToPage }] = usePage();
  const omitRoutes = useTrialUserOmitRoutes();
  const classes = useStyles({ isMainPage });

  const practiceBook = books?.find((b) => b.title.includes(practiceBookType));

  const handleToggle = (bookType) => {
    if (bookType && bookType !== practiceBookType) {
      setPracticeBookType(bookType);
    }
  };

  const handleOnNavToPage = (pageNumber: number) => {
    setCharacter(null);
    const pageCharacter = findPageAndPopulatePageData(books?.[0]?.pages, pageNumber)
      ?.characters?.[0];
    pageCharacter && setCharacter(pageCharacter);

    goToPage(pageNumber);
  };

  const showPdf = useMemo(() => !!practiceBook?.url, [practiceBook]);

  useEffect(() => {
    if (!isMainPage) refetch();
  }, [isMainPage]);

  const { PageList, pagesToDisplay } = useLetterTeaching(practiceBook, isMainPage);

  const toggleItems = [
    {
      value: StudentPracticeBookLevel.Emerging,
      text: tA11y('buttons.emerging'),
    },
    {
      value: StudentPracticeBookLevel.Developing,
      text: tA11y('buttons.developing'),
    },
  ];

  useEffect(() => {
    createTelemetryEvent();
  }, [currentPage, practiceBook, noOfPages]);

  const createTelemetryEvent = () => {
    if (!practiceBook) return;

    const page = pagesToDisplay?.[0]?.[0] ?? pagesToDisplay?.[0];
    if (!page) return;

    let foundCharacter = character ? { ...character } : null;
    if (!foundCharacter || isEmpty(foundCharacter)) {
      foundCharacter = {
        ...practiceBook.characters?.find((c) => c.character === props?.letter?.toUpperCase()),
      } as any;
    }

    fireEvent({
      bookId: practiceBook.id,
      bookType: BookType.PRACTICE_BOOK,
      bookCode: practiceBook.bookCode,
      pageId: page.id,
      pageNumber: `${page.pageNumber}`,
      pageLayout: noOfPages === 2 ? PageLayout.DOUBLE_PAGE : PageLayout.SINGLE_PAGE,
      section: isMainPage ? ApplicationSection.PRACTICE_BOOKS : ApplicationSection.LETTER_LEARNING,
      ...(foundCharacter &&
        !isEmpty(foundCharacter) && { character: formatCharacter(foundCharacter) }),
      route: (location?.state as any)?.from || RouteSource.DASHBOARD,
    });
  };

  const BookContent = (
    <MainActivityPagingControls
      isMainPage={isMainPage}
      pagination={false}
      displayZoomControls
      hasFooter
      hasCover={isMainPage}
      bookLayout={Boolean(!showPdf)}
      height="80vh"
    >
      {showPdf ? (
        <DoublePagePdf
          paperWrapper={!!showPdf}
          leftPage={{
            documentProps: { file: practiceBook?.url },
            pageProps: {
              pageNumber: getPageNumber('leftPage', pagesToDisplay),
            },
          }}
          rightPage={{
            documentProps: { file: practiceBook?.url },
            pageProps: {
              pageNumber: getPageNumber('rightPage', pagesToDisplay),
            },
          }}
        />
      ) : (
        PageList
      )}
    </MainActivityPagingControls>
  );

  const PageFooter = (
    <SectionFooter>
      <Box className={classes.footer}>
        <ToggleButtonGroup
          selectedValue={practiceBookType}
          onChange={handleToggle}
          ariaLabel={practiceBookType}
          items={toggleItems}
        />
      </Box>
      {isMainPage && practiceBook?.id && (
        <Box className={classes.pageNavigation}>
          <PageNavigation
            activePages={getActivePages(currentPage, pagesToDisplay)}
            bookId={practiceBook.id}
            hasLetterNavigation={isMainPage}
            onNavToPage={handleOnNavToPage}
            pageOffset={practiceBook.offset}
            totalPages={totalPages}
          />
        </Box>
      )}
    </SectionFooter>
  );

  return (
    <WithQuery loading={loading} error={error}>
      {isMainPage ? (
        <ActivityHeaderLayout
          title={t('practiceBook')}
          hasTogglePageView
          Footer={PageFooter}
          showCloseButton={false}
          omitRoutes={omitRoutes}
        >
          {BookContent}
        </ActivityHeaderLayout>
      ) : (
        <>
          {BookContent}
          {PageFooter}
        </>
      )}
    </WithQuery>
  );
};

const useStyles = makeStyles((theme) => ({
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(4),
  },
  pageNavigation: {
    left: (props: any) => `calc(50% + ${props.isMainPage ? '166px' : '88px'})`,
    position: 'absolute',
    top: remCalc(19),
  },
  icon: {
    fontSize: '8px !important',
  },
}));

export default PracticeBook;
